// =============================================================================
// ui/tabs/ExitModelEditor.tsx — the Edit Exit Model modal.
// -----------------------------------------------------------------------------
// Transcribed from Josh's screenshot: a stats strip, IDENTITY (name), and
// EXECUTION RULES (stop / target / scale-out), with Delete · Cancel · Save.
//
// ── WHY THIS DOES NOT HYDRATE, WHERE THE TRADE FORM MUST (D71) ──
//
// The trade form re-reads its record because it edits 31 of 48 columns and a
// partial read would blank the rest. An exit model has FOUR fields and this form
// renders every one, so the row already on screen IS the whole row — there is
// nothing a fuller read could add. The safety property comes from the shape of
// the table rather than from a guard.
//
// ⚠ That is the thing to recheck if `exit_models` ever gains a column. A fifth
// field this form does not render turns the save into exactly the write D46
// forbids, and nothing here would notice.
//
// ── 🚨 DELETING UNTAGS TRADES ──
//
// `trades.exit_model_id` is `ON DELETE SET NULL`, so a delete does not fail when
// trades reference the model — it silently strips the tag from every one of
// them. The confirmation says so, with the count, because re-creating a model
// with the same name does not bring the tags back.
// =============================================================================

import { useCallback, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { useTabMemoryDraft } from '../state/tabMemory';
import { useExitModelCap } from '../state/useFreeTierCap';
import { FreeTierLimit } from '../shell/FreeTierLimit';
import {
  deleteExitModel, insertExitModel, saveExitModel, validateExitModel,
  type ExitModelPatch,
} from '../../io/sync/saveExitModel';
import type { ExitModelSummary } from '../../io/sync/exitModels';
import type { LeaderboardStats } from '../../selectors/exitModelBoard';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { pct, threeWay, unitCompact } from '../format';

export interface ExitModelEditorProps {
  /** `null` opens a blank form — the New Exit Model path. */
  model: ExitModelSummary | null;
  /** Null for a new model, which has no trades yet by definition. */
  stats: LeaderboardStats | null;
  /** How many trades carry this model — the delete confirmation needs it. */
  taggedTrades: number;
  /**
   * The display unit, PASSED IN rather than read from a hook.
   *
   * 🚨 So the strip and the card behind it are formatting the same value with
   * the same unit (#67) — and so this modal keeps working in a test that renders
   * it on its own, which a hook into the preferences provider would have broken.
   */
  unit: PnlUnit;
  newId: () => string;
  onSaved: () => void;
  onDeleted: () => void;
  onCancel: () => void;
}

export function ExitModelEditor(
  { model, stats, taggedTrades, unit, newId, onSaved, onDeleted, onCancel }: ExitModelEditorProps,
) {
  const { sb, userId } = useSupabase();
  const isNew = model == null;

  // ── 🔴 THE DRAFT OUTLIVES THE TAB, BUT CANCEL STILL DISCARDS IT (TCR-80) ──
  //
  // `AppShell` destroys the tab on every switch (D94), so this modal and
  // everything typed into it used to go with it — three rules gone, no prompt,
  // no trace. Josh's rule is that nothing a trader types should ever be lost.
  //
  // ⚠ AND YET AUTOSAVE IS THE WRONG ANSWER HERE, which is the part worth
  // reading. Every surface in this client that commits on blur is an in-page
  // card; this is a modal with a Cancel that DISCARDS. Writing on blur would
  // make that button a lie. So the draft is kept above the tab and the database
  // is still only touched by Save. Decided by Josh, 2026-10-06.
  //
  // ⚠ Keyed PER MODEL, so two models cannot inherit each other's half-finished
  // rules, and `new` is its own slot.
  //
  // ⚠ A reload loses it. `tabMemory` is session-scoped on purpose — its header
  // says why — and that is the accepted limit of this approach.
  const [values, setValues, discardDraft] = useTabMemoryDraft<ExitModelPatch>(
    `exitmodel:draft:${model?.id ?? 'new'}`,
    {
      name: model?.name ?? '',
      stopRule: model?.stopRule ?? '',
      targetRule: model?.targetRule ?? '',
      scaleOutRule: model?.scaleOutRule ?? '',
    },
  );

  /** Throw the draft away, then leave — Cancel, the ×, and the cap's close. */
  const cancel = useCallback(() => { discardDraft(); onCancel(); }, [discardDraft, onCancel]);
  const [busy, setBusy] = useState(false);
  // 🚨 THE FREE-TIER CAP — #65. Consumed by the refusal branch below.
  const { blocked: capped } = useExitModelCap();
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof ExitModelPatch>(k: K, v: ExitModelPatch[K]) =>
    setValues((cur) => ({ ...cur, [k]: v }));

  const save = useCallback(async () => {
    const invalid = validateExitModel(values);
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    setError(null);
    const res = isNew
      ? await insertExitModel(sb, newId(), values, userId)
      : await saveExitModel(sb, model.id, values, userId);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    // ⚠ The draft is now redundant; leaving it would restore stale text over a
    // row that already has it.
    discardDraft();
    onSaved();
  }, [sb, userId, values, isNew, model, newId, onSaved, discardDraft]);

  const remove = useCallback(async () => {
    if (!model) return;
    // ⚠ The COUNT is in the sentence. "Delete this exit model?" understates it —
    // the FK strips the tag from every trade that used it, and no undo brings
    // those back.
    const tail = taggedTrades
      ? `\n\n${taggedTrades} trade${taggedTrades === 1 ? '' : 's'} tagged with it will be left `
        + 'with no exit model. That cannot be undone by re-creating it.'
      : '\n\nNo trades are tagged with it.';
    if (!window.confirm(`Delete "${model.name}"?${tail}`)) return;
    setBusy(true);
    setError(null);
    const res = await deleteExitModel(sb, userId, model.id);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    discardDraft();
    onDeleted();
  }, [sb, userId, model, taggedTrades, onDeleted, discardDraft]);

  // ── 🚨 THE CAP REFUSES BEFORE THE FORM, NOT AFTER IT — #65 ──
  //
  // Same shape as the pattern editor, and for the same reason: this is the only
  // caller of `insertExitModel`, which holds the only insert into `exit_models`.
  //
  // ⚠ The exit-model limit is deliberately EQUAL to the pattern limit. The
  // pattern form requires an exit model, so capping one and not the other just
  // moves the wall — the old client's suite asserts the two are equal to each
  // other, not merely that each is 3.
  //
  // ⚠ `isNew` only. ⚠ INERT while PAID_LAUNCH is false.
  if (isNew && capped) {
    return <FreeTierLimit title="New exit model" message={capped} onClose={cancel} />;
  }

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Edit Exit Model">
      <div className="modal em-modal">
        <header className="modal-head">
          <b>{isNew ? 'New Exit Model' : 'Edit Exit Model'}</b>
          <button type="button" className="btn" onClick={cancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body">
          {/* ⚠ Only for a model that HAS a history. A new one shows nothing rather
              than four zeros, which would read as a model that has been used and
              lost money. */}
          {stats && !isNew ? (
            <div className="trade-stats-strip em-stats">
              <Stat label="Sample" value={String(stats.sample)} />
              <Stat label="Win Rate" value={stats.sample ? pct(stats.winRate, 0) : '—'} />
              {/* 🚨 THE SAME FORMATTER AS THE CARD BEHIND THIS MODAL (#67).
                  These two figures and the card's are ONE number. The strip used
                  `money()` while the card used `unitCompact()`, so opening a
                  model on Points showed "+$1,085" over a card still reading
                  "+1,085 pts" — two renderings of one fact, disagreeing, six
                  inches apart. Beta formats both with `fmtPnlUnit(…, unit, 0)`. */}
              {/* ⚠ THE COLOUR IS INSIDE THE SAMPLE GUARD, and it was not. With
                  no trades the value is an em-dash and `stats.expectancy` is 0,
                  so the old `>= 0` painted THE EM-DASH GREEN — an untraded model
                  reading as a winning one. Zero is also neutral, not green: a
                  model that has made exactly nothing has not made money. */}
              <Stat
                label="Expectancy" value={stats.sample ? unitCompact(stats.expectancy, unit) : '—'}
                cls={stats.sample ? threeWay(stats.expectancy) : ''}
              />
              <Stat
                label="Total P&L" value={stats.sample ? unitCompact(stats.totalPnl, unit) : '—'}
                cls={stats.sample ? threeWay(stats.totalPnl) : ''}
              />
            </div>
          ) : null}

          <h4 className="tw2-head">Identity</h4>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              value={values.name} autoFocus
              onChange={(e) => set('name', e.target.value)}
            />
          </label>

          <h4 className="tw2-head">Execution Rules</h4>
          {/* ⚠ All three may be empty. Josh's DISCRETIONARY model has the literal
              words "No rules" typed into every one of them — that is content he
              wrote, so nothing here supplies a placeholder that could be mistaken
              for it or saved in its place. */}
          <label className="field">
            <span className="field-label">Stop Rule</span>
            <textarea
              rows={3} value={values.stopRule}
              onChange={(e) => set('stopRule', e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Target Rule</span>
            <textarea
              rows={3} value={values.targetRule}
              onChange={(e) => set('targetRule', e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Scale Out Rule</span>
            <textarea
              rows={3} value={values.scaleOutRule}
              onChange={(e) => set('scaleOutRule', e.target.value)}
            />
          </label>

          {error ? (
            <div role="alert" className="import-alert">
              <b>Nothing was saved.</b> {error}
            </div>
          ) : null}
        </div>

        <footer className="modal-foot import-row">
          {!isNew ? (
            <button type="button" className="btn danger" disabled={busy} onClick={() => void remove()}>
              Delete
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn" disabled={busy} onClick={cancel}>Cancel</button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="stat-cell">
      <div className="slabel">{label}</div>
      <div className={`sval ${cls ?? ''}`}>{value}</div>
    </div>
  );
}
